import clsx from 'clsx';
import { it } from 'date-fns/locale/it';
import { useState } from 'react';
import DatePicker, { CalendarContainer, registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { colors } from '../../color';
import { CalendarIcon } from '../../icons/calendarIcon';
import { ChevronIcon } from '../../icons/chevronIcon';
import { ClockIcon } from '../../icons/clockIcon';
import './style.css';

registerLocale('it', it);

interface Props {
    startDate: Date | null;
    setStartDate: (date: Date | null) => void;
    label?: string;
    error?: boolean;
    disabled?: boolean;
    supportingText?: string
    type: 'date' | 'time'
    timeIntervals?: number
    includeTimes?: Date[]
    onMonthChange?: (month: number) => void
    onYearChange?: (year: number) => void
    required?: boolean
    placeholderText?: string
}

export function Calendar(props: Props) {
    const { startDate, setStartDate, label, error, disabled, supportingText, type, timeIntervals, includeTimes, required, placeholderText } = props;
    const [open, setOpen] = useState(false);
    const customLocale = {
        ...it,
        options: {
            ...it.options,
            weekStartsOn: 1 as const,
        },
        localize: {
            ...it.localize,
            day: (n: number) => ['D', 'L', 'M', 'M', 'G', 'V', 'S'][n]
        }
    };

    registerLocale('it-custom', customLocale);
    //@ts-ignore
    const MyContainer = ({ className, children }) => (
        <CalendarContainer className={className + ' bg-white border border-neutral-100 p-3 shadow-sm gap-1 flex rounded-lg'}>
            <div style={{ position: "relative", backgroundColor: 'white' }}>{children}</div>
        </CalendarContainer>
    )

    return (
        <div className='flex flex-col w-full gap-2'>
            {
                label &&
                <span className='text-label-sm text-neutral-700 font-semibold'>{label + (label && required ? ' *' : '')}</span>
            }
            <div className={clsx('h-10 flex flex-row-reverse border rounded-lg px-3 py-[10px] w-full', {
                "bg-white": !disabled,
                'border-red-300 focus:border-red-300': error,
                'border-neutral-300  focus:border-neutral-300': !error,
                'bg-neutral-100': disabled,
            })}>
                <DatePicker
                    onMonthChange={(month) => props.onMonthChange && props.onMonthChange(month.getMonth())}
                    onYearChange={(year) => props.onYearChange && props.onYearChange(year.getFullYear())}
                    includeTimes={includeTimes}
                    locale="it-custom"
                    selected={startDate}
                    onChange={(date) => { setStartDate(date); setOpen(false) }}
                    dateFormat={type === 'date' ? "dd/MM/yyyy" : 'HH:mm'}
                    showPopperArrow={false}
                    timeIntervals={timeIntervals}
                    placeholderText={placeholderText ? placeholderText : type === 'date' ? 'gg/mm/aaaa' : 'hh:mm'}
                    wrapperClassName='w-full flex justify-center'
                    calendarClassName="bg-white"
                    calendarContainer={MyContainer}
                    calendarIconClassName='p-0'
                    showTimeSelect={type === 'time'}
                    showTimeSelectOnly={type === 'time'}
                    open={open}
                    disabled={disabled}
                    onClickOutside={() => setOpen(false)}
                    showIcon
                    toggleCalendarOnIconClick
                    className={clsx("h-full font-[Inter] w-full border-0 outline-none p-0 text-text-sm", {
                        "bg-neutral-100": disabled
                    })}
                    renderDayContents={(day, date) => {
                        return <div className='h-6 w-6 rounded-full flex items-center justify-center m-0'>{day}</div>;
                    }}
                    icon={type === 'date' ?
                        <CalendarIcon color={colors.neutral[500]} size={20} onClick={(e) => disabled ? undefined : setOpen(open ? false : true)} /> :
                        <ClockIcon color={colors.neutral[500]} size={20} onClick={(e) => disabled ? undefined : setOpen(open ? false : true)} />
                    }
                    renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => (
                        <div className='bg-white flex items-center justify-between'>
                            <span className='ps-1 capitalize font-[Inter] text-text-md font-semibold text-black'>{date.toLocaleString('en-EN', { month: 'long', year: 'numeric' })}</span>
                            <div className='flex gap-1'>
                                <button onClick={decreaseMonth}><ChevronIcon color={colors.neutral[500]} size={24} direction='l' /></button>
                                <button onClick={increaseMonth}><ChevronIcon color={colors.neutral[500]} size={24} direction='r' /></button>
                            </div>
                        </div>
                    )}
                    dayClassName={(date) =>
                        date.getDay() === 0 ? "text-red-500" : ""
                    }
                />
            </div>
            {supportingText && (
                <span
                    className={clsx('text-text-sm', {
                        'text-red-500': error,
                        'text-neutral-400': !error,
                    })}
                >
                    {supportingText}
                </span>
            )}
        </div>
    );
}

